import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';

import {
  trunkStatusGet,
  trunkTestPromptsGet,
  trunkTestCallStart,
  trunkTestCallResult
} from '../../../store/slices/tenantSlices/configurationSlice';

const POLL_MS = 2000;
const POLL_LIMIT = 40; // ~80s, comfortably past the 45s originate timeout

// Colour + icon per live gateway state.
const STATUS_UI = {
  connected: { color: '#2cd07e', icon: 'CheckCircle' },
  not_connected: { color: '#ffc107', icon: 'AlertTriangle' },
  down: { color: '#f62d51', icon: 'XCircle' },
  not_provisioned: { color: '#f62d51', icon: 'XCircle' },
  unknown: { color: '#6c757d', icon: 'HelpCircle' }
};

const RESULT_UI = {
  dialing: { color: '#2cabe3', title: 'Dialing…' },
  success: { color: '#2cd07e', title: 'Success' },
  failed: { color: '#f62d51', title: 'Failed' }
};

// Place one real call over this trunk that plays a WAV on answer, so an admin
// can prove the trunk works without a softphone. Deliberately not WebRTC: this
// screen is reachable by master admin, who has no agent extension to register.
const TestCallModal = ({ isOpen, trunk, onCloseClick }) => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [callerId, setCallerId] = useState('');
  const [destination, setDestination] = useState('');
  const [promptId, setPromptId] = useState('default');
  const [result, setResult] = useState(null);
  const [calling, setCalling] = useState(false);
  const [error, setError] = useState('');

  const pollRef = useRef(null);
  const pollCount = useRef(0);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollCount.current = 0;
  };

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await dispatch(trunkStatusGet(trunk.id)).unwrap();
      setStatus(res);
    } catch (e) {
      console.log('error', e);
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  const loadPrompts = async () => {
    try {
      const res = await dispatch(
        trunkTestPromptsGet(trunk?.company_id ? { company_id: trunk.company_id } : undefined)
      ).unwrap();
      setPrompts((res?.data || []).map((p) => ({ label: p.name, value: String(p.id) })));
    } catch (e) {
      console.log('error', e);
    }
  };

  useEffect(() => {
    if (isOpen && trunk?.id) {
      setResult(null);
      setError('');
      setPromptId('default');
      setDestination('');
      // A sensible default: trunks that authenticate by username dial out as it.
      setCallerId(trunk?.username || trunk?.from_user || '');
      loadStatus();
      loadPrompts();
    }
    return stopPolling;
    // Intentionally keyed on open/trunk only: loadStatus and loadPrompts are
    // recreated every render, so including them would refetch in a loop.
  }, [isOpen, trunk?.id]);

  const poll = (uuid) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      pollCount.current += 1;
      try {
        const res = await dispatch(trunkTestCallResult(uuid)).unwrap();
        const r = res?.result;
        if (r) setResult(r);
        if (r && r.state !== 'dialing') {
          stopPolling();
          setCalling(false);
        }
      } catch (e) {
        // A 404 means the result expired; stop rather than hammer the API.
        stopPolling();
        setCalling(false);
      }
      if (pollCount.current >= POLL_LIMIT) {
        stopPolling();
        setCalling(false);
        setResult((prev) =>
          prev && prev.state === 'dialing'
            ? {
                ...prev,
                state: 'failed',
                title: 'No result',
                reason: 'The call did not report a result in time. Check the trunk on the switch.'
              }
            : prev
        );
      }
    }, POLL_MS);
  };

  const onTestCall = async () => {
    setError('');
    const dest = String(destination || '').trim();
    if (!dest) {
      setError('Enter a test destination number');
      return;
    }
    if (dest.replace(/\D/g, '').length < 4) {
      setError('That destination number looks too short');
      return;
    }

    setCalling(true);
    setResult({ state: 'dialing', title: 'Dialing…', reason: `Calling ${dest}…` });
    try {
      const res = await dispatch(
        trunkTestCallStart({
          id: trunk.id,
          destination_number: dest,
          caller_id: callerId,
          prompt_id: promptId
        })
      ).unwrap();

      if (res?.trunk_status) setStatus(res.trunk_status);

      if (res?.started === false) {
        setResult(res.result);
        setCalling(false);
        return;
      }
      if (res?.uuid) {
        if (res?.result) setResult(res.result);
        poll(res.uuid);
      } else {
        setCalling(false);
        setResult({ state: 'failed', title: 'Failed', reason: 'Could not start the test call.' });
      }
    } catch (e) {
      console.log('error', e);
      setCalling(false);
      setResult(null);
    }
  };

  const close = () => {
    stopPolling();
    setCalling(false);
    onCloseClick();
  };

  const s = STATUS_UI[status?.state] || STATUS_UI.unknown;
  const StatusIcon = Icon[s.icon] || Icon.HelpCircle;
  const r = RESULT_UI[result?.state] || RESULT_UI.failed;

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Test Call — {trunk?.name}</div>
      </ModalHeader>

      <ModalBody className="px-4">
        {/* ---- live trunk status ---- */}
        <div
          className="d-flex align-items-center gap-3 p-3 mb-3 rounded"
          style={{ backgroundColor: `${s.color}1a`, borderLeft: `3px solid ${s.color}` }}>
          {statusLoading ? (
            <Spinner size="sm" />
          ) : (
            <StatusIcon size={22} style={{ color: s.color, flexShrink: 0 }} />
          )}
          <div className="flex-grow-1">
            <div className="fw-bold" style={{ color: s.color }}>
              {statusLoading ? 'Checking trunk…' : status?.label || 'Unknown'}
            </div>
            <div style={{ fontSize: '12.5px' }}>
              {statusLoading ? 'Querying FreeSWITCH for the live gateway state.' : status?.detail}
            </div>
            {!statusLoading && status?.gateway && (
              <div className="text-muted" style={{ fontSize: '11.5px' }}>
                Gateway {status.gateway}
                {status.sip_state ? ` · SIP ${status.sip_state}` : ''}
                {status.ping_ms ? ` · ping ${status.ping_ms} ms` : ''}
              </div>
            )}
          </div>
          <Button
            size="sm"
            className="bg-transparent text-primary border-0"
            disabled={statusLoading}
            onClick={loadStatus}>
            <Icon.RefreshCw size={15} />
          </Button>
        </div>

        {/* ---- inputs ---- */}
        <Row>
          <Col lg="4">
            <FormInput
              name="caller_id"
              label="Caller ID"
              placeholder="Number shown to the destination"
              value={callerId}
              onChange={(e) => setCallerId(e.target.value)}
            />
          </Col>
          <Col lg="4">
            <FormInput
              name="destination_number"
              label="Test Destination Number"
              placeholder="e.g. 919876543210"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="prompt_id"
              label="Message to play"
              placeholder="Select audio"
              value={promptId}
              options={prompts}
              onChange={(e) => setPromptId(e.target.value)}
              menuPlacement="bottom"
            />
          </Col>
        </Row>

        {error && (
          <div className="text-danger" style={{ fontSize: '13px' }}>
            {error}
          </div>
        )}

        <div className="text-muted" style={{ fontSize: '12.5px' }}>
          Places one real call over this trunk. When the destination answers, the selected message
          is played. Test calls are not charged against VoiceBroadcast credit.
        </div>

        {/* ---- result ---- */}
        {result && (
          <div
            className="d-flex align-items-start gap-3 p-3 mt-3 rounded"
            style={{ backgroundColor: `${r.color}1a`, borderLeft: `3px solid ${r.color}` }}>
            {result.state === 'dialing' ? (
              <Spinner size="sm" style={{ color: r.color }} />
            ) : result.state === 'success' ? (
              <Icon.PhoneCall size={20} style={{ color: r.color, flexShrink: 0 }} />
            ) : (
              <Icon.PhoneOff size={20} style={{ color: r.color, flexShrink: 0 }} />
            )}
            <div>
              <div className="fw-bold" style={{ color: r.color }}>
                {result.title || r.title}
              </div>
              <div style={{ fontSize: '13px' }}>{result.reason}</div>
              {result.cause && (
                <div className="text-muted mt-1" style={{ fontSize: '11.5px' }}>
                  Hangup cause: {result.cause}
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button onClick={close} className="px-5 bg-transparent text-primary">
          Close
        </Button>
        <Button
          onClick={onTestCall}
          disabled={calling || statusLoading}
          className="px-5 bg-primary border-primary">
          {calling ? 'Calling…' : 'Test Call'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

TestCallModal.propTypes = {
  isOpen: PropTypes.bool,
  trunk: PropTypes.any,
  onCloseClick: PropTypes.func
};

export default TestCallModal;
